import { CommonModule, Location } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardComponent } from '@custom-components/general/card/card.component';
import { LinkButtonComponent } from '@custom-components/general/button';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { ListRowComponent } from '@custom-components/general/list-row';
import { NoItemsFoundComponent } from '@custom-components/general/no-items-found/no-items-found.component';
import { TablePaginationComponent } from '@custom-components/general/table-pagination/table-pagination.component';
import { TabBarComponent } from '@custom-components/layout/tab-bar/tab-bar.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import {
  INSTALLATION_GROUP_DETAIL_TABS_VIEW,
  INSTALLATION_GROUP_INSTALLATIONS_TAB,
} from '../../constants/installation-group-detail-tabs.constant';
import { APP_PATHS, PAGINATION_DEFAULT, XEMEX_OID } from '@core/constants';
import {
  canViewInstallations,
  installationAccessKnown,
} from '@core/helpers/installation-access.helper';
import { InstallationGroupStore } from '@root/app/store/installation-group.store';
import { AuthStore } from '@root/app/store/auth.store';
import { UserStore } from '@root/app/store/user.store';
import {
  RemoveGroupMemberDialogComponent,
  RemoveGroupMemberDialogData,
} from '../../components/remove-group-member-dialog/remove-group-member-dialog.component';
import {
  AddDevicesDialogComponent,
  AddDevicesDialogData,
} from '../../components/add-devices-dialog/add-devices-dialog.component';
import {
  EditInstallationGroupDialogComponent,
  EditInstallationGroupDialogData,
} from '../../components/edit-installation-group-dialog/edit-installation-group-dialog.component';
import {
  AddGroupToPartnershipDialogComponent,
  AddGroupToPartnershipDialogData,
} from '../../components/add-group-to-partnership-dialog/add-group-to-partnership-dialog.component';
import {
  AddLocationsDialogComponent,
  AddLocationsDialogData,
} from '../../components/add-locations-dialog/add-locations-dialog.component';
import {
  GroupInstallationRow,
  GroupInstallationsComponent,
} from '../../components/group-installations/group-installations.component';
import { addressLine } from '@core/helpers/address.helper';
import { getUserActivePartner, AuditLogEntry, PartnershipSummary, UpdateInstallationGroup } from '@core/models';
import { TabViewModel } from '@core/models/tab-view.model';
import { filter } from 'rxjs';
import { RecentlyVisitedService } from '@core/services';

@Component({
  selector: 'xemex-installation-group-detail',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    PageHeaderComponent,
    PageBodyComponent,
    TabBarComponent,
    CardGridComponent,
    CardComponent,
    DetailFieldComponent,
    ListRowComponent,
    NoItemsFoundComponent,
    TablePaginationComponent,
    LinkButtonComponent,
    GroupInstallationsComponent,
  ],
  templateUrl: './installation-group-detail.component.html',
})
export class InstallationGroupDetailComponent {
  readonly installationGroupStore = inject(InstallationGroupStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly routerLocation = inject(Location);
  private readonly translate = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);
  private readonly recentlyVisited = inject(RecentlyVisitedService);
  private recentlyVisitedRecordedGroupOid: string | undefined;

  protected readonly pageSize = PAGINATION_DEFAULT.limit;
  protected readonly selectedTab = signal('general');

  /** Tabs with live device/location counts shown next to their labels. */
  protected readonly tabs = computed<TabViewModel[]>(() =>
    INSTALLATION_GROUP_DETAIL_TABS_VIEW.map(tab => {
      // Hide the count until the first fetch resolves so the tab doesn't briefly show 0.
      if (tab.route === 'devices') {
        return {
          ...tab,
          count: this.installationGroupStore.isLoadingDevices() ? undefined : this.deviceCount(),
        };
      }
      if (tab.route === 'locations') {
        return {
          ...tab,
          count: this.installationGroupStore.isLoadingLocations()
            ? undefined
            : this.locationCount(),
        };
      }
      return tab;
    }),
  );
  protected readonly showInstallations = computed(() =>
    canViewInstallations(this.userStore.user(), this.authStore.permissions()),
  );

  protected readonly tabsWithInstallations = computed<TabViewModel[]>(() => [
    ...this.tabs(),
    {
      ...INSTALLATION_GROUP_INSTALLATIONS_TAB,
      count: this.installationGroupStore.isLoadingInstallations()
        ? undefined
        : this.installationCount(),
    },
  ]);
  private readonly groupOid = signal('');

  protected readonly devicesPage = signal(1);
  protected readonly locationsPage = signal(1);
  protected readonly auditLogPage = signal(1);
  protected readonly installationsPage = signal(1);

  protected readonly breadcrumbs = computed<BreadcrumbItem[]>(() => [
    { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
    {
      label: this.translate.instant('sidebarMenu.installationGroups'),
      routerLink: ['/', APP_PATHS.InstallationGroups],
    },
    { label: this.installationGroupStore.detail()?.name ?? '—' },
  ]);

  /** Enabled feature names for the user's active partner (or via Xemex). */
  private readonly features = computed(() => {
    const partnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    return (
      this.authStore
        .permissions()
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? []
    );
  });

  /** True when the user may manage this group: has the permission and their partner owns it. */
  protected readonly canManage = computed(() => {
    const detail = this.installationGroupStore.detail();
    if (detail?.accessVia !== 'owner') {
      return false;
    }
    return this.features().includes('manage_installation_groups');
  });

  /** True when the user holds the view_audit_log feature for their partner (or via Xemex). */
  protected readonly canViewAuditLog = computed(() => this.features().includes('view_audit_log'));

  /** Partnerships this group is shared through (caller-visible only). */
  protected readonly partnerships = computed(
    () => this.installationGroupStore.detail()?.partnerships ?? [],
  );

  /**
   * True when the user may attach/detach this group to/from partnerships: they must own the group
   * AND hold the manage_partnerships feature (or be Xemex, which carries the feature group-wide).
   */
  protected readonly canManagePartnerships = computed(() => {
    const detail = this.installationGroupStore.detail();
    if (detail?.accessVia !== 'owner') {
      return false;
    }
    return this.features().includes('manage_partnerships');
  });

  protected readonly auditLog = computed(() => this.installationGroupStore.auditLog());
  protected readonly auditLogCount = computed(() => this.installationGroupStore.auditLogCount());
  protected readonly auditLogTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.auditLogCount() / this.pageSize)),
  );

  protected readonly devices = computed(() => this.installationGroupStore.devices());
  protected readonly deviceCount = computed(
    () => this.installationGroupStore.devices().numberOfItems,
  );
  protected readonly deviceTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.deviceCount() / this.pageSize)),
  );

  protected readonly locations = computed(() =>
    this.installationGroupStore.locations().locations.map(loc => ({
      ...loc,
      address: [loc.street, loc.streetNumber, loc.streetNumberAddition]
        .filter(part => part !== null && part !== undefined && `${part}` !== '')
        .join(' '),
      postalCity: [loc.postalcode, loc.city].filter(Boolean).join(', '),
    })),
  );
  protected readonly locationCount = computed(
    () => this.installationGroupStore.locations().numberOfItems,
  );
  protected readonly locationTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.locationCount() / this.pageSize)),
  );

  protected readonly installationRows = computed<GroupInstallationRow[]>(() => {
    const groupOwnerOid = this.installationGroupStore.detail()?.ownerPartnerOid;
    return this.installationGroupStore.installations().installations.map(member => {
      const viaDeviceId = member.via === 'device' ? (member.viaDeviceId ?? null) : null;
      return {
        installationOid: member.installationOid,
        name: member.name,
        status: member.status,
        address: addressLine({ ...member.location, postalCode: member.location.postalcode }),
        partnerName: member.partnerName ?? '',
        ownedByOtherPartner: !!groupOwnerOid && member.partnerOid !== groupOwnerOid,
        locationId: member.location.locationId,
        viaDeviceId,
        viaDeviceLabel: member.viaDeviceLdn || `${viaDeviceId ?? ''}`,
      };
    });
  });
  protected readonly installationCount = computed(
    () => this.installationGroupStore.installations().numberOfItems,
  );

  constructor() {
    this.route.params.subscribe(params => {
      this.selectedTab.set(params['tab'] ?? 'general');

      const groupOid = params['installationGroupOid'];
      // Only (re)initialize when the group actually changes — switching tabs changes the `tab`
      // param but keeps the same group, so it must not trigger a refetch.
      if (groupOid && groupOid !== this.groupOid()) {
        this.groupOid.set(groupOid);
        this.devicesPage.set(1);
        this.locationsPage.set(1);
        this.auditLogPage.set(1);
        this.installationsPage.set(1);
        this.installationGroupStore.getInstallationGroup(groupOid);
        this.fetchDevices(1);
        this.fetchLocations(1);
      }
    });

    // Load the audit-log page reactively once the OID is known and the user is permitted to view it.
    effect(() => {
      const oid = this.groupOid();
      const page = this.auditLogPage();
      if (oid && this.canViewAuditLog()) {
        this.installationGroupStore.getAuditLog({
          installationGroupOid: oid,
          limit: this.pageSize,
          offset: page - 1,
        });
      }
    });

    // Only users who are offered the Installations tab request its rows.
    effect(() => {
      const oid = this.groupOid();
      const page = this.installationsPage();
      if (oid && this.showInstallations()) {
        this.installationGroupStore.getInstallationGroupInstallations({
          groupOid: oid,
          limit: this.pageSize,
          offset: page - 1,
        });
      }
    });

    // A link to the Installations tab can reach a user who may not open it, whose page body would
    // stay empty. Once their profile says so, they land on General instead.
    effect(() => {
      const user = this.userStore.user();
      const permissions = this.authStore.permissions();
      if (
        this.selectedTab() === INSTALLATION_GROUP_INSTALLATIONS_TAB.route &&
        installationAccessKnown(user, permissions) &&
        !canViewInstallations(user, permissions)
      ) {
        this.selectedTab.set('general');
        void this.router.navigate(['/', APP_PATHS.InstallationGroups, this.groupOid(), 'general'], {
          replaceUrl: true,
        });
      }
    });

    // Record the visit once the group's detail (name) has actually loaded.
    effect(() => {
      const detail = this.installationGroupStore.detail();
      const oid = this.groupOid();
      if (detail && oid && this.recentlyVisitedRecordedGroupOid !== oid) {
        this.recentlyVisitedRecordedGroupOid = oid;
        this.recentlyVisited.record({
          type: 'installationGroup',
          id: oid,
          icon: 'workspaces',
          primary: detail.name,
          secondary: this.translate.instant('recentlyUsed.types.installationGroup'),
          route: ['/', APP_PATHS.InstallationGroups, oid],
        });
      }
    });
  }

  onClickBack(): void {
    this.routerLocation.back();
  }

  onTabChange(route: string): void {
    this.selectedTab.set(route);
  }

  onViewOwnerPartner(): void {
    const ownerPartnerOid = this.installationGroupStore.detail()?.ownerPartnerOid;
    if (ownerPartnerOid) {
      this.router.navigate(['/', APP_PATHS.Partners, ownerPartnerOid]);
    }
  }

  onViewPartnership(): void {
    const viaPartnershipOid = this.installationGroupStore.detail()?.viaPartnershipOid;
    if (viaPartnershipOid) {
      this.router.navigate(['/', APP_PATHS.Partnerships, viaPartnershipOid]);
    }
  }

  onViewDevice(deviceId: number): void {
    this.router.navigate(['/', APP_PATHS.Devices, deviceId]);
  }

  onViewLocation(locationId: number): void {
    this.router.navigate(['/', APP_PATHS.Locations, locationId]);
  }

  onViewInstallation(installationOid: string): void {
    void this.router.navigate(['/', APP_PATHS.Installations, installationOid]);
  }

  onViewPartner(partnerOid: string | undefined): void {
    if (partnerOid) {
      this.router.navigate(['/', APP_PATHS.Partners, partnerOid]);
    }
  }

  onViewPartnershipById(partnershipOid: string): void {
    this.router.navigate(['/', APP_PATHS.Partnerships, partnershipOid]);
  }

  /** The partner on the other side of the partnership from the group's owner. */
  protected partnershipPartnerName(partnership: PartnershipSummary): string {
    const ownerPartnerOid = this.installationGroupStore.detail()?.ownerPartnerOid;
    return partnership.ownerPartnerOid === ownerPartnerOid
      ? partnership.guestPartnerName
      : partnership.ownerPartnerName;
  }

  onAddPartnership(): void {
    const detail = this.installationGroupStore.detail();
    if (!detail) {
      return;
    }
    this.dialog
      .open(AddGroupToPartnershipDialogComponent, {
        width: '40rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          ownerPartnerOid: detail.ownerPartnerOid,
          existingPartnershipOids: this.partnerships().map(p => p.partnershipOid),
        } satisfies AddGroupToPartnershipDialogData,
      })
      .afterClosed()
      .pipe(filter((partnershipOid): partnershipOid is string => !!partnershipOid))
      .subscribe(partnershipOid => {
        this.installationGroupStore.addGroupToPartnership({
          groupOid: this.groupOid(),
          partnershipOid,
        });
      });
  }

  onRemovePartnership(partnership: PartnershipSummary, event: MouseEvent): void {
    event.stopPropagation();
    this.openRemoveDialog('partnership', this.partnershipPartnerName(partnership)).subscribe(() => {
      this.installationGroupStore.removeGroupFromPartnership({
        groupOid: this.groupOid(),
        partnershipOid: partnership.partnershipOid,
      });
    });
  }

  onAuditLogPageChange(page: number): void {
    this.auditLogPage.set(page);
  }

  /**
   * For an INSTALLATION_GROUP_UPDATED entry, the fields that were changed paired with their new
   * values (status is localized, blanks shown as a dash). Empty for any other event type.
   */
  protected updatedFields(entry: AuditLogEntry): { label: string; value: string }[] {
    if (entry.eventType !== 'INSTALLATION_GROUP_UPDATED') {
      return [];
    }
    const fields = (entry.eventData as { updated_fields?: Record<string, unknown> } | null)
      ?.updated_fields;
    if (!fields) {
      return [];
    }
    return Object.entries(fields).map(([key, value]) => ({
      label: this.translate.instant(`installationGroups.audit.fields.${key}`),
      value: this.formatFieldValue(key, value),
    }));
  }

  private formatFieldValue(key: string, value: unknown): string {
    if (value === null || value === undefined || `${value}`.trim() === '') { // NOSONAR
      return '—';
    }
    if (key === 'status') {
      return this.translate.instant(`installationGroups.edit.statuses.${value}`); // NOSONAR
    }
    return `${value}`;// NOSONAR
  }

  /** Icon + label for an audit entry's linkable resource, or null when it isn't navigable. */
  protected resourceLink(entry: AuditLogEntry): { icon: string; label: string } | null {
    if (!entry.resourceId) {
      return null;
    }
    const label = (type: string) =>
      `${this.translate.instant(`installationGroups.audit.resource.${type}`)} ${entry.resourceId}`;// NOSONAR
    switch (entry.resourceType) {
      case 'Device':
        return { icon: 'router', label: label('Device') };
      case 'Location':
        return { icon: 'home_work', label: label('Location') };
      default:
        return null;
    }
  }

  /** Navigate to the device or location referenced by an audit entry's resource. */
  protected onViewResource(entry: AuditLogEntry): void {
    const id = Number(entry.resourceId);
    if (Number.isNaN(id)) {
      return;
    }
    if (entry.resourceType === 'Device') {
      this.onViewDevice(id);
    } else if (entry.resourceType === 'Location') {
      this.onViewLocation(id);
    }
  }

  /** Human-readable label for an audit event type, falling back to a prettified form. */
  protected eventLabel(eventType: string): string {
    const key = `installationGroups.audit.events.${eventType}`;
    const translated = this.translate.instant(key);
    if (translated !== key) {
      return translated;
    }
    const words = eventType.toLowerCase().replaceAll('_', ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  onDevicesPageChange(page: number): void {
    this.devicesPage.set(page);
    this.fetchDevices(page);
  }

  onLocationsPageChange(page: number): void {
    this.locationsPage.set(page);
    this.fetchLocations(page);
  }

  onInstallationsPageChange(page: number): void {
    this.installationsPage.set(page);
  }

  onEdit(): void {
    const detail = this.installationGroupStore.detail();
    if (!detail) {
      return;
    }
    this.dialog
      .open(EditInstallationGroupDialogComponent, {
        width: '40rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          name: detail.name,
          description: detail.description,
          status: detail.status,
        } satisfies EditInstallationGroupDialogData,
      })
      .afterClosed()
      .pipe(
        filter(
          (changes): changes is UpdateInstallationGroup =>
            !!changes && Object.keys(changes).length > 0,
        ),
      )
      .subscribe(changes => {
        this.installationGroupStore.updateInstallationGroup({ groupOid: this.groupOid(), changes });
      });
  }

  onAddDevice(): void {
    const detail = this.installationGroupStore.detail();
    if (!detail) {
      return;
    }
    this.dialog
      .open(AddDevicesDialogComponent, {
        width: '52rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          groupOid: this.groupOid(),
          ownerPartnerOid: detail.ownerPartnerOid,
        } satisfies AddDevicesDialogData,
      })
      .afterClosed()
      .pipe(filter((added): added is true => added === true))
      .subscribe(() => this.devicesPage.set(1));
  }

  onAddLocation(): void {
    const detail = this.installationGroupStore.detail();
    if (!detail) {
      return;
    }
    this.dialog
      .open(AddLocationsDialogComponent, {
        width: '52rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: {
          groupOid: this.groupOid(),
          ownerPartnerOid: detail.ownerPartnerOid,
        } satisfies AddLocationsDialogData,
      })
      .afterClosed()
      .pipe(filter((added): added is true => added === true))
      .subscribe(() => this.locationsPage.set(1));
  }

  onRemoveDevice(deviceId: number, label: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openRemoveDialog('device', label).subscribe(() => {
      this.installationGroupStore.removeDeviceFromGroup({
        groupOid: this.groupOid(),
        deviceId,
        offset: this.devicesPage() - 1,
      });
    });
  }

  onRemoveLocation(locationId: number, label: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openRemoveDialog('location', label).subscribe(() => {
      this.installationGroupStore.removeLocationFromGroup({
        groupOid: this.groupOid(),
        locationId,
        offset: this.locationsPage() - 1,
      });
    });
  }

  private openRemoveDialog(mode: RemoveGroupMemberDialogData['mode'], label: string) {
    return this.dialog
      .open(RemoveGroupMemberDialogComponent, {
        width: '32rem',
        maxWidth: 'calc(100vw - 2rem)',
        data: { mode, label } satisfies RemoveGroupMemberDialogData,
      })
      .afterClosed()
      .pipe(filter((confirmed): confirmed is true => confirmed === true));
  }

  private fetchDevices(page: number): void {
    this.installationGroupStore.getInstallationGroupDevices({
      groupOid: this.groupOid(),
      limit: this.pageSize,
      offset: page - 1,
    });
  }

  private fetchLocations(page: number): void {
    this.installationGroupStore.getInstallationGroupLocations({
      groupOid: this.groupOid(),
      limit: this.pageSize,
      offset: page - 1,
    });
  }
}
